/**
 * AddCommentInput Component - Unified design from GoalDetailScreen
 * Input form for adding or editing comments
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AddCommentInputProps } from './types';

// ============================================================
// SVG Icon Components
// ============================================================

const CheckIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// AddCommentInput Component
// ============================================================

const AddCommentInput: React.FC<AddCommentInputProps> = ({
  onSubmit,
  onCancel,
  editingComment,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const [text, setText] = useState('');

  // Theme colors
  const themeColors = {
    cardBackground: isDark ? colors.card : '#F1F5F9',
    textPrimary: colors.textPrimary,
    separator: isDark ? colors.border : '#E2E8F0',
    iconDefault: colors.iconDefault,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
  };

  // Initialize text when editingComment changes
  useEffect(() => {
    if (editingComment) {
      setText(editingComment.text);
    } else {
      setText('');
    }
  }, [editingComment]);

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
      setText('');
    }
  };

  const handleCancel = () => {
    setText('');
    onCancel();
  };

  return (
    <View style={[styles.addCommentCard, { backgroundColor: themeColors.cardBackground }]}>
      {/* Header */}
      <View style={styles.addCommentHeader}>
        <Text style={[styles.addCommentHeaderText, { color: themeColors.textPrimary }]}>
          {editingComment ? t('comments.editComment') : t('comments.addComment')}
        </Text>
      </View>

      {/* Separator */}
      <View style={[styles.addCommentSeparator, { backgroundColor: themeColors.separator }]} />

      {/* Input area */}
      <View style={styles.addCommentInputContainer}>
        <TextInput
          style={[styles.addCommentInput, { color: themeColors.textPrimary }]}
          value={text}
          onChangeText={setText}
          placeholder={t('comments.placeholder')}
          placeholderTextColor={themeColors.placeholder}
          multiline
          textAlign="right"
        />
      </View>

      {/* Separator */}
      <View style={[styles.addCommentSeparator, { backgroundColor: themeColors.separator }]} />

      {/* Action buttons */}
      <View style={styles.addCommentActions}>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleCancel}>
          <XIcon size={16} color={themeColors.iconDefault} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleSubmit}>
          <CheckIcon size={16} color={themeColors.iconDefault} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Add Comment Input styles (matches Figma node 2569:211200)
  addCommentCard: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  addCommentHeader: {
    paddingVertical: 2,
    alignItems: 'flex-end',
  },
  addCommentHeaderText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  addCommentSeparator: {
    height: 1,
    backgroundColor: '#E2E8F0',
  },
  addCommentInputContainer: {
    minHeight: 40,
    paddingVertical: 4,
  },
  addCommentInput: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 32,
    padding: 0,
  },
  addCommentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 2,
  },
  addCommentActionButton: {
    padding: 4,
  },
});

export default AddCommentInput;
